import { createContext, useState, useEffect, useContext, useRef } from "react";
import { useMetamask } from "use-metamask";
import Web3 from "web3";
import WalletConnectProvider from "@walletconnect/web3-provider";
import { useLatestCallback } from "../helpers/util";
import { config } from "../config";
import AlertContext from "./AlertContext";
import ConnectWalletPopup from "../components/ConnectWalletPopup";

function networkName(id: "localhost" | number) {
  switch (id === "localhost" ? id : Number(id)) {
    case 1:
      return "Main";
    case 3:
      return "Ropsten";
    case 4:
      return "Rinkeby";
    case 5:
      return "Goerli";
    case 42:
      return "Kovan";
    case "localhost":
      return "localhost";
    default:
      return "local";
  }
}

export type UserWalletContextType = {
  isConnected: boolean;
  walletType: string;
  chainId: void | number | string;
  account: void | string;
  web3: void | import("web3").default;
};

const UserWalletContext = createContext(
  {} as {
    userWallet: UserWalletContextType;
    setUserWallet: React.Dispatch<React.SetStateAction<UserWalletContextType>>;
    btnText: "INSTALL" | "CONNECT WALLET" | "WRONG NETWORK" | "";
    clickHandler: () => void;
  }
);

export const UserWalletProvider = (props) => {
  const { children } = props;
  const [userWallet, setUserWallet] = useState({
    isConnected: false,
    walletType: "",
    chainId: undefined,
    account: undefined,
    web3: null,
  });
  let { connect, metaState } = useMetamask();

  const [connectPopupOpen, setConnectPopupOpen] = useState(false);
  const [btnText, setBtnText] = useState<
    "INSTALL" | "CONNECT WALLET" | "WRONG NETWORK" | ""
  >("INSTALL");
  const [error, setError] = useState({});
  void error;

  const { dialog } = useContext(AlertContext);
  const errorAlertRef = useRef<
    ReturnType<typeof dialog.alert> | ReturnType<typeof dialog.confirm>
  >(undefined);

  const networkId = +config.networkId;

  const checkStatus = () => {
    if (!userWallet.isConnected) {
      if (errorAlertRef.current) errorAlertRef.current.close();
      setBtnText("CONNECT WALLET");
      setError({});
      return;
    }
    if (userWallet.chainId) {
      if (+userWallet.chainId !== +networkId) {
        setError({
          message: `Please connect to the ${networkName(
            networkId
          )} Ethereum network.`,
          type: "error",
          code: 1000,
        });
        setBtnText("WRONG NETWORK");
        return;
      }
    }
    setBtnText("");

    setError({});
  };

  const connectWallet = useLatestCallback(
    async (type: "metamask" | "connectwallet" = "metamask") => {
      if (type === "connectwallet") {
        try {
          const provider = new WalletConnectProvider({
            infuraId: "2ce4bcae45b74bc49f749a12b61b920d", // Required
          });

          provider.on("accountsChanged", (accounts) => {
            setUserWallet({
              ...userWallet,
              account: accounts[0],
            });
          });

          // Subscribe to chainId change
          provider.on("chainChanged", (chainId) => {
            setUserWallet({
              ...userWallet,
              chainId: "" + chainId,
            });
          });

          // Subscribe to session connection/open
          provider.on("open", () => {});

          // Subscribe to session disconnection/close
          provider.on("close", (code, reason) => {
            setUserWallet({
              isConnected: false,
              walletType: "",
              chainId: undefined,
              account: undefined,
              web3: null,
            });
          });

          await provider.enable();
          // @ts-ignore
          const web3 = new Web3(provider);
          setUserWallet({
            isConnected: provider.connected,
            walletType: "connectwallet",
            chainId: "" + provider.chainId,
            account: provider.accounts[0],
            web3: web3,
          });
          if (provider.connected) {
            setConnectPopupOpen(false);
          }
        } catch (error) {}
      } else if (metaState.isAvailable && !metaState.isConnected) {
        try {
          await connect(Web3);
        } catch (error) {
          console.warn(error);
        }
      }
    }
  );

  useEffect(() => {
    checkStatus();
  }, [userWallet.chainId, userWallet.account, userWallet.isConnected]);

  // auto connect
  // const isAutoConnectCalledRef = useRef(false);
  // useEffect(() => {
  //   if (isAutoConnectCalledRef.current) return;
  //   isAutoConnectCalledRef.current = true;
  //   connect(Web3).catch(err => console.warn(err));
  // }, [metaState.isAvailable && !metaState.isConnected]);
  useEffect(() => {
    if (metaState.isConnected) {
      setConnectPopupOpen(false);
    }
  }, [metaState.isConnected]);

  const clickHandler = useLatestCallback(async () => {
    if (btnText === "INSTALL") {
      window.open("https://metamask.io/download.html", "_blank");
    } else if (btnText === "CONNECT WALLET") {
      setConnectPopupOpen(true);
    } else if (btnText === "WRONG NETWORK") {
      if (userWallet.chainId !== +networkId && userWallet.web3) {
        try {
          const provider = userWallet.web3.currentProvider;
          // @ts-ignore
          await provider.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: Web3.utils.toHex(networkId) }],
          });
        } catch (switchError) {
          // This error code indicates that the chain has not been added to MetaMask.
          if (switchError.code === 4902) {
            errorAlertRef.current = dialog.alert(
              `Please add ${networkName(
                networkId
              )} Ethereum network with id of ${networkId} to metamask.`
            );
          }
        }
      }
    }
  });

  useEffect(() => {
    if (metaState.isConnected) {
      setUserWallet({
        isConnected: metaState.isConnected,
        walletType: "metamask",
        chainId: metaState.chain.id,
        account: metaState.account[0],
        web3: metaState.web3,
      });
    } else {
      setUserWallet({
        isConnected: false,
        walletType: "",
        chainId: undefined,
        account: undefined,
        web3: null,
      });
    }
  }, [
    metaState.chain.id,
    metaState.account,
    metaState.isConnected,
    metaState.web3,
  ]);

  return (
    <UserWalletContext.Provider
      value={{
        userWallet,
        setUserWallet,
        btnText,
        clickHandler,
      }}
    >
      {children}
      <ConnectWalletPopup
        connectPopupOpen={connectPopupOpen}
        setConnectPopupOpen={setConnectPopupOpen}
        onClick={connectWallet}
      />
    </UserWalletContext.Provider>
  );
};

export const UserWalletConsumer = UserWalletContext.Consumer;

export default UserWalletContext;
